/**
 * @file RepTonnesTrendWidget.js
 * @version 1.3.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-03
 * @modified 2026-10-08
 *
 * A rep's tonnage by month, with the headline figure and a year-on-year change.
 *
 * The number comes from utils/tonnage.js on the server and is NOT a sum of
 * invoice quantities — read that file before questioning a figure.
 */

import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Filler, Tooltip
} from 'chart.js';
import useRepDashboard, { formatVolume, volumeFigure, formatCompactMoney } from './useRepDashboard';
import RepRefreshButton from './RepRefreshButton';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import useRepMobile from '../hooks/useRepMobile';
import { ACCOUNT_NAMES } from '../utils/accountCodes';
import useThemeColors from '../hooks/useThemeColors';
import './RepWidgets.css';

/*
 * Bulk feed: compound, blend, bulk straights, mineral, fat. The codes whose
 * quantity IS a tonnage, so a tonne on the axis means a tonne sold.
 *
 * Mirrors BULK_SALES_ACCOUNT_CODES in utils/tonnage.js, which is where the
 * server decides the same thing. Duplicated rather than fetched because it is
 * the card's opening state and must not wait on a round trip — if the two ever
 * disagree the server still wins, since it resolves the unit.
 */
const DEFAULT_CODES = ['201', '202', '204', '206', '209'];

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

function RepTonnesTrendWidget({ variant = 'desktop' }) {
  /*
   * Bulk feed by default.
   *
   * The unfiltered view mixes tonnes of compound with counts of colostrum and
   * doses of additive, and reports the lot as tonnes — so the headline was a
   * number nobody could act on. Bulk is what a rep sells and what a tonne
   * actually means.
   */
  const [accountIds, setAccountIds] = useState([]);
  const [accountCodes, setAccountCodes] = useState(DEFAULT_CODES);
  const repMobile = useRepMobile();

  const params = useMemo(() => ({
    months: 12,
    ...(accountIds.length ? { accountIds: accountIds.join(',') } : {}),
    ...(accountCodes.length ? { accountCodes: accountCodes.join(',') } : {})
  }), [accountIds, accountCodes]);

  const { data, loading, message, refresh } = useRepDashboard('tonnage', { params });

  // The patch's customers and the codes they have actually bought against.
  // Loaded once and reused by both pickers; a rep who sells no bagged feed is
  // not offered the bag codes.
  const { data: refData } = useRepDashboard('accounts');

  /*
   * The live theme, and it now actually follows the toggle.
   *
   * This read the variables in a useMemo with no dependencies, under a comment
   * claiming it followed the light/dark switch. It did not: a canvas keeps the
   * colours it was painted with, so the chart stayed in the old palette until
   * some unrelated change forced a rebuild. useThemeColors watches data-theme
   * and hands back a new object when it moves.
   */
  const colors = useThemeColors({
    primary: '--color-primary',
    primaryRgb: '--color-primary-rgb',
    text: '--text-primary',
    border: '--border-primary'
  });

  // What the server decided this selection is measured in — 'T' for feed,
  // 'totes' / 'bags' / 'units' for the codes where mass says nothing.
  const unitLabel = data?.unit?.label || 'T';

  const chart = useMemo(() => {
    if (!data?.months?.length) return null;
    return {
      data: {
        labels: data.months.map(m => m.label),
        /*
         * Two lines, two axes.
         *
         * Volume is the headline, but it is the half that can come back as
         * nothing: an account code whose items carry no weight has no tonnage
         * to plot, and a flat zero line reads as "sold nothing" rather than
         * "not measured that way". Money is always known, so the card always
         * says something true.
         *
         * Value is a plain line rather than a second filled area — two fills
         * on one chart obscure each other wherever they cross.
         */
        datasets: [
          {
            label: unitLabel,
            yAxisID: 'y',
            data: data.months.map(m => (m.volume ?? m.tonnes)),
            borderColor: colors.primary,
            backgroundColor: `rgba(${colors.primaryRgb}, 0.15)`,
            borderWidth: 2,
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: colors.primary
          },
          {
            label: 'Sold',
            yAxisID: 'yValue',
            data: data.months.map(m => m.value),
            borderColor: colors.text,
            borderWidth: 1.5,
            borderDash: [4, 3],
            fill: false,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: colors.text
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          // Two lines need saying which is which, and the card has no room for
          // a caption elsewhere.
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              color: colors.text,
              boxWidth: 10,
              boxHeight: 2,
              font: { size: 10 },
              padding: 8,
              usePointStyle: false
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => (ctx.datasetIndex === 0
                ? formatVolume(ctx.parsed.y, unitLabel)
                : formatCompactMoney(ctx.parsed.y))
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            border: { color: colors.border },
            ticks: {
              color: colors.text,
              font: { size: 10 },
              // A phone cannot carry twelve month labels; show every third.
              callback(value, index) {
                if (variant === 'phone' && index % 3 !== 0) return '';
                return this.getLabelForValue(value);
              },
              maxRotation: 0,
              autoSkip: false
            }
          },
          y: {
            position: 'left',
            beginAtZero: true,
            grid: { color: colors.border, drawBorder: false },
            border: { display: false },
            ticks: { color: colors.text, font: { size: 10 }, maxTicksLimit: 4 }
          },
          // Its own scale on the right. Sharing one would flatten tonnes into
          // the axis line, since the money figures are four orders larger.
          yValue: {
            position: 'right',
            beginAtZero: true,
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: colors.text,
              font: { size: 10 },
              maxTicksLimit: 4,
              callback: (v) => formatCompactMoney(v)
            }
          }
        }
      }
    };
  }, [data, colors, variant, unitLabel]);

  const change = data?.comparison?.changePct;

  /*
   * '' is the "no filter" row in both, matching what the state already holds —
   * SearchableDropdown takes a value of '' or null as a real sentinel, so there
   * is nothing to translate on the way in or out.
   */
  // No "All" row: with a multi-select, selecting nothing already means all, and
  // an "All" option that has to be un-picked when a real one is chosen is a
  // second rule for the same thing.
  const customerFilterOptions = useMemo(() => [
    ...(refData?.accounts || []).map((a) => ({
      value: String(a.id),
      label: a.companyName,
      badge: a.accountNo || '',
      searchText: `${a.accountNo || ''} ${a.companyName}`
    }))
  ], [refData]);

  const productFilterOptions = useMemo(() => [
    ...(refData?.salesAccountCodes || []).map((code) => ({
      value: String(code),
      label: ACCOUNT_NAMES[code] || String(code),
      badge: String(code),
      searchText: `${code} ${ACCOUNT_NAMES[code] || ''}`
    }))
  ], [refData]);

  return (
    <div className="dashboard-card rep-widget rep-tonnes-card">
      <div className="card-header">
        <h3>
          <span className="material-symbols-rounded">balance</span>  
          Your Sales
        </h3>
        <div className="header-controls">
          {data && <span className="rep-widget-sub">12 months to last month</span>}
          <RepRefreshButton
            cachedAt={data?.cachedAt}
            loading={loading}
            onRefresh={refresh}
          />
        </div>
      </div>
      <div className="card-body">
        {/* "Loading…" only on the FIRST load. Every pick in a filter reloads
            the card, and this used to swap the whole body — the filters
            included — for the loading line. The multi-select was unmounted
            mid-selection and came back closed, so it looked as though it shut
            on every click and could only ever take one choice. The previous
            figures now stay up, dimmed, until the new ones land. */}
        {loading && !data && <div className="rep-widget-state">Loading…</div>}
        {!loading && message && <div className="rep-widget-state">{message}</div>}
        {!message && data && (
          <>
            <div className={`rep-headline${loading ? ' is-reloading' : ''}`}>
              {/* The unit rides with the figure, because it changes: a
                  selection of tote codes counts totes, not tonnes, and a bare
                  number would silently mean something different. */}
              <div className="rep-headline-figure">
                {volumeFigure(data.totals.volume ?? data.totals.tonnes, unitLabel)}
                <span className="rep-headline-unit">{unitLabel}</span>
              </div>
              <div className="rep-headline-meta">
                <span>{formatCompactMoney(data.totals.value)}</span>
                {/* No percentage against a zero baseline — the server sends
                    null rather than an infinite "up 100%". */}
                {change !== null && change !== undefined && (
                  <span className={`rep-delta ${change >= 0 ? 'up' : 'down'}`}>
                    <span className="material-symbols-rounded">
                      {change >= 0 ? 'trending_up' : 'trending_down'}
                    </span>
                    {Math.abs(change)}% vs last year
                  </span>
                )}
              </div>
            </div>
            {/* Multi-select, searchable: a rep's patch runs to a couple of
                hundred accounts, and picking exactly one product type was the
                wrong shape — bulk feed is five codes, not one. */}
            <div className="rep-tonnes-filters">
              <MultiSelectDropdown
                value={accountIds}
                onChange={setAccountIds}
                options={customerFilterOptions}
                placeholder="All customers"
                searchable
                searchPlaceholder="Search by # or name…"
                boundarySelector=".dashboard-home, .content-inner"
                escapeOverflow
                /* Both menus are the card's content width, so they occupy the
                   same box — each still pins to its own side so a clamped
                   width opens inward rather than drifting off the card. */
                align="left"
                widthSelector=".rep-tonnes-card"
                hideTags
                summarize={(v) => (v.length === 0 ? 'All customers'
                  : v.length === 1
                    ? (customerFilterOptions.find(o => o.value === v[0])?.label || '1 customer')
                    : `${v.length} customers`)}
              />
              <MultiSelectDropdown
                value={accountCodes}
                onChange={setAccountCodes}
                options={productFilterOptions}
                placeholder="All product types"
                /* No search box on a phone. There are a dozen product types and
                   they fit on one screen, so the field bought nothing and cost
                   the keyboard covering the list it was meant to filter. The
                   customer filter above keeps its search: a rep's patch is a
                   couple of hundred accounts and scrolling that is no answer. */
                searchable={!repMobile}
                searchPlaceholder="Search product types…"
                boundarySelector=".dashboard-home, .content-inner"
                escapeOverflow
                align="right"
                widthSelector=".rep-tonnes-card"
                hideTags
                summarize={(v) => (v.length === 0 ? 'All product types'
                  : v.length === 1
                    ? (productFilterOptions.find(o => o.value === v[0])?.label || v[0])
                    : `${v.length} product types`)}
              />
            </div>
            <div className={`rep-chart-holder${loading ? ' is-reloading' : ''}`}>
              {chart && <Line data={chart.data} options={chart.options} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RepTonnesTrendWidget;
